import styled from 'styled-components';
import { compose, variant } from 'styled-system';

import { theme } from '../../../common/ui/theme';
import { buttonShadows } from '../../Button/components/buttonShadows';
import { IconButtonProps } from '../IconButton';

const iconColorVariant = variant({
    prop: 'destructive',
    variants: {
        false: {
            color: theme.colors.foreground.subtle,

            '&:hover:not(:disabled), &:active:not(:disabled)': {
                color: theme.colors.foreground.default,
            },
        },
        true: {
            color: theme.colors.negative.foreground,
        },
    },
});

const buttonEmphasisVariant = variant({
    prop: 'emphasis',
    variants: {
        medium: {
            background: `linear-gradient(0deg, ${theme.colors.background.subtle}, ${theme.colors.background.subtle}, ${theme.colors.background.default})`,
            backgroundSize: '200% 200%',
            backgroundPosition: '0% 0%',
            boxShadow: `${buttonShadows.drop}, ${theme.shadows.border.default}`,
            transition: 'background-position 100ms ease-out, box-shadow 100ms ease-in-out',

            '&:hover:not(:disabled)': {
                backgroundPosition: '100% 100%',
                boxShadow: `${buttonShadows.drop}, ${buttonShadows.borderHover}, ${buttonShadows.dropHover}`,
            },
            '&:focus:not(:disabled)': {
                boxShadow: `${theme.shadows.focus}, ${buttonShadows.drop}, ${buttonShadows.borderHover}, ${buttonShadows.dropHover}`,
            },
            '&:active:not(:disabled)': {
                background: theme.colors.background.inset,
                boxShadow: `${theme.shadows.focus}, ${buttonShadows.drop}, ${theme.shadows.border.default}, ${buttonShadows.dropHover}`,
            },
        },
        none: {
            background: 'transparent',
            transition: 'background-color 100ms ease-out',

            '&:hover:not(:disabled)': {
                backgroundColor: theme.colors.background.subtle,
            },
            '&:focus:not(:disabled)': {
                backgroundColor: theme.colors.background.subtle,
                boxShadow: `${theme.shadows.focus}, ${buttonShadows.drop}, ${buttonShadows.borderHover}, ${buttonShadows.dropHover}`,
            },
            '&:active:not(:disabled)': {
                backgroundColor: theme.colors.background.inset,
                boxShadow: `${theme.shadows.focus}, ${buttonShadows.drop}, ${theme.shadows.border.default}, ${buttonShadows.dropHover}`,
            },
        },
        transparent: {
            background: 'transparent',

            '&:hover:not(:disabled)': {
                backgroundColor: 'transparent',
            },

            '&:active:not(:disabled)': {
                backgroundColor: 'transparent',
            },
        },
    },
});

const buttonVariantStyle = variant({
    prop: 'variant',
    variants: {
        default: {
            borderRadius: '6px',
        },
        rounded: {
            borderRadius: '50%',
        },
    },
});

export const ButtonWrapper = styled.button<{
    size: number;
    destructive: IconButtonProps['destructive'];
    emphasis: IconButtonProps['emphasis'];
    variant: IconButtonProps['variant'];
}>`
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    outline: none;
    width: ${(props) => props.size}px;
    height: ${(props) => props.size}px;

    ${compose(iconColorVariant, buttonEmphasisVariant, buttonVariantStyle)};

    &:disabled {
        color: ${theme.colors.foreground.disabled};
        background: transparent;
    }
`;
